import { type DataFunctionArgs, json } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { RoleEnum } from 'types/enums.ts'
import { MainContainer } from '~/components/layout/main-container.tsx'
import { MainLayout } from '~/components/layout/main-layout.tsx'
import {
  FormatList,
  LegalPage,
  Title,
  TwoSignatures,
} from '~/components/ui/legalPage.tsx'
import { Spacer } from '~/components/ui/spacer.tsx'
import { getUserId } from '~/features/auth/auth.server.ts'
import { getCompanyAlertsNumber } from '~/features/smartConnect/companyAlertsNumber.server.ts'
import { prisma } from '~/utils/config/db.server.ts'
import { capitalizeWords } from '~/utils/strings.ts'
import { MODULE_ACCESS_SELECT } from '~/features/accessControl/modules.ts'

export async function loader({ request }: DataFunctionArgs) {
  const userId = await getUserId(request)

  if (!userId) {
    return json({
      user: null,
      alertsNumber: null,
    })
  }

  const user = await prisma.user.findUnique({
    where: {
      id: userId,
    },
    select: {
      firstName: true,
      fullName: true,
      company: {
        select: {
          name: true,
          ...MODULE_ACCESS_SELECT,
        },
      },
    },
  })

  const alertsNumber = await getCompanyAlertsNumber(userId)

  return json({
    user,
    alertsNumber,
  })
}

export default function Confidentiality() {
  const { user, alertsNumber } = useLoaderData<typeof loader>()

  if (user) {
    return (
      <MainLayout
        userName={capitalizeWords(user.firstName)}
        userRole={RoleEnum.USER}
        alertsNumber={alertsNumber}
        access={user.company!}
      >
        <MainContainer>
          <div className="flex w-full justify-center px-0 pb-4 pt-7 md:pl-20 md:pr-5">
            <ConfidentialityPageData user={user} />
          </div>
        </MainContainer>
      </MainLayout>
    )
  }

  return (
    <div className="mx-auto mt-8 max-w-[1300px] px-5 sm:mt-16 sm:px-10 md:px-20">
      <Link
        to={'/'}
        className={`mx-auto mt-2 font-semibold text-foreground-muted`}
      >
        {`<`} Volver a Inicio
      </Link>
      <Spacer size="5xs" />
      <ConfidentialityPageData />
    </div>
  )
}

function ConfidentialityPageData({
  user,
}: {
  user?: {
    company: {
      name: string
    } | null
    fullName: string
  }
}) {
  return (
    <LegalPage title="ACUERDO DE CONFIDENCIALIDAD" className="max-w-[1300px]">
      <p>
        De una parte, D. Sergio Arnaldos, con N.I.F. número 45794390X, en nombre
        y representación, en su condición de Administrador Solidario, de la
        entidad Traacking World Services 22 SL, con C.I.F. número B44545408 (en
        adelante, la “<strong>PARTE RECEPTORA</strong>”).
      </p>
      <p>
        De otra, el Usuario en Representación de su Empresa (en adelante, la “
        <strong>PARTE EMISORA</strong>”).
      </p>
      <p>
        Ambas partes manifiestan tener y se reconocen recíprocamente capacidad
        legal suficiente para suscribir el presente acuerdo y
      </p>
      <Title title="MANIFIESTAN" isCenter />
      <FormatList
        style="roman"
        dotsClassName="min-w-[2.3rem] sm:min-w-[3rem]"
        items={[
          'Que la PARTE EMISORA es una compañía usuaria de servicios logísticos y está interesada en analizar una posible mejora de los costes asociados a los mismos.',
          <p>
            Que la PARTE RECEPTORA, por su parte, es una compañía dedicada a la
            consultoría en el sector logístico y ha desarrollado una aplicación
            que permite a las empresas optimizar los costes asociados a sus
            servicios logísticos (en adelante, la “<strong>APLICACIÓN”</strong>
            ).
          </p>,
          'Que, en aras a valorar una posible colaboración entre las Partes, la PARTE EMISORA está interesada en utilizar la APLICACIÓN a modo de prueba, a cuyos efectos proporcionará a la PARTE RECEPTORA a través de la APLICACIÓN cierta información y documentación estrictamente confidencial perteneciente a la PARTE EMISORA.',
          <p>
            Que, atendido el carácter confidencial y secreto de dicha
            información y documentación, las partes convienen que la misma debe
            ser tratada con la más estricta confidencialidad, y en virtud de lo
            anterior, ambas partes suscriben el presente{' '}
            <strong>ACUERDO DE CONFIDENCIALIDAD</strong> (en adelante, el “
            <strong>Acuerdo</strong>”), que se regirá por los siguientes{' '}
          </p>,
        ]}
      />
      <Title title="PACTOS" isCenter />
      <Title title="PRIMERO.	OBJETO" isSubtitle spacer={false} />
      <p>
        Constituye objeto del presente Acuerdo mantener la confidencialidad de
        toda la información que la PARTE EMISORA proporcione a la PARTE
        RECEPTORA a través de la APLICACIÓN con el fin de analizar la posible
        optimización de los costes asociados a los servicios logísticos de la
        PARTE EMISORA (el “<strong>Objeto</strong>”).
      </p>
      <Title title="SEGUNDO.	INFORMACION CONFIDENCIAL" isSubtitle />
      <FormatList
        style="numbered"
        symbol="2."
        items={[
          'La PARTE RECEPTORA guardará total y estricta confidencialidad y no divulgará, sin el previo consentimiento por escrito de la PARTE EMISORA, ningún dato ni información suministrada directa o indirectamente por ella.',
          'Para los efectos del presente Acuerdo se considera “Información Confidencial”, toda y cualquier información suministrada a través de la APLICACIÓN por parte de la PARTE EMISORA, que incluye, pero no está limitada a, información histórica, técnica, informática, industrial, administrativa, societaria, económica, financiera, contable, de operación, estudios y cualquier otro tipo de información que se maneje en relación con la PARTE EMISORA y/o el Objeto.',
          'En consecuencia, la Información Confidencial será mantenida y utilizada por la PARTE RECEPTORA con total confidencialidad y reserva, y no será, total y parcialmente difundida, entregada, mostrada, proporcionada, suministrada o, en general, revelada a terceros distintos de los representantes o asesores de éste involucrados en el Objeto y que estén expresamente autorizados para recibirla.',
          'La PARTE RECEPTORA deberá usar el mismo grado y medidas de cuidado y salvaguarda para evitar la revelación o el uso de la Información Confidencial que emplea con respecto a información de su propiedad de igual importancia.',
          <p>
            No será considerada como Información Confidencial:
            <div className="ml-4 sm:ml-10">
              <FormatList
                style="alphabetical"
                items={[
                  'Aquella información o documento que al tiempo de ser revelada estuviera legítimamente a disposición del público en general sin que medie violación de las obligaciones de confidencialidad que son materia de este Acuerdo.',
                  'Aquella información que la PARTE RECEPTORA haya adquirido legítimamente de terceros sin que al tiempo de ser revelada haya mediado violación de las obligaciones de confidencialidad que son materia de este Acuerdo.',
                  'Aquella información que la PARTE RECEPTORA haya adquirido o desarrollado de manera independiente sin que al tiempo de ser revelada haya mediado incumplimiento de las obligaciones de confidencialidad que son materia de este Acuerdo.',
                ]}
              />
            </div>
          </p>,
          'Si la PARTE RECEPTORA resultase legalmente compelida por autoridad competente a revelar cualquier Información Confidencial recibida, deberá dentro de lo permitido por la ley, dar a aviso a la PARTE EMISORA a fin de que ésta adopte las medidas legales que considere pertinentes. ',
        ]}
      />
      <Title title="TERCERO.	GARANTÍA" isSubtitle />
      <p>
        La PARTE RECEPTORA garantiza que cualesquiera personas que, con la
        finalidad descrita en el Pacto anterior, puedan tener acceso a la
        Información Confidencial, tanto asesores como cualesquiera otras
        personas que le presten servicios, se hallan obligadas a mantener
        confidencial la misma, respondiendo la PARTE RECEPTORA directamente del
        cumplimiento de la obligación de confidencialidad por parte de dichas
        personas.
      </p>
      <Title
        title="CUARTO.	    PROPIEDAD DE LA INFORMACIÓN CONFIDENCIAL Y PROPIEDAD INDUSTRIAL"
        isSubtitle
      />
      <p>
        La PARTE EMISORA es y seguirá siendo en todo momento la propietaria
        exclusiva de la Información Confidencial y de los derechos de propiedad
        industrial que utiliza para el desarrollo de su actividad.
      </p>
      <p>
        El presente Acuerdo no implica en modo alguno licencia, autorización o
        cesión de derechos de propiedad industrial, por lo que la PARTE
        RECEPTORA no podrá utilizar, bajo ningún concepto y en ningún modo,
        directo o indirecto, el nombre, la imagen o cualesquiera marcas o
        nombres comerciales, registrados o no, incluyendo sus logotipos,
        utilizadas o pertenecientes a la PARTE EMISORA.
      </p>
      <Title
        title="QUINTO.    CONSERVACIÓN DE LA INFORMACIÓN CONFIDENCIAL"
        isSubtitle
      />
      <p>
        Tras el cumplimiento del Objeto, la PARTE RECEPTORA podrá conservar la
        Información Confidencial que tenga en su poder únicamente para usos
        estadísticos, es decir, de forma agregada y anónima para realización de
        análisis, informes y mejora de la PLATAFORMA, sin identificar
        individualmente a ninguna persona física o jurídica.
      </p>
      <Title title="SEXTO.     PERDURABILIDAD DEL ACUERDO" isSubtitle />
      <p>
        Las obligaciones de confidencialidad recogidas en el presente Acuerdo
        seguirán siendo válidas y exigibles hasta transcurridos 1 año desde la
        firma del mismo.
      </p>
      <Title
        title="SÉPTIMO.   LEY APLICABLE Y JURISDICCIÓN COMPETENTE."
        isSubtitle
      />
      <p>
        El presente Acuerdo se regirá e interpretará conforme a la normativa
        española.
      </p>
      <p>
        Para cualquier cuestión o litigio que pueda surgir de la interpretación,
        aplicación o ejecución del presente Acuerdo, ambas partes, con expresa
        renuncia al fuero propio si lo tuvieren, se someten a la jurisdicción de
        los Juzgados y Tribunales de Barcelona.
      </p>
      <p>
        En prueba de conformidad con cuanto antecede, ambas partes contratantes
        firman, por duplicado y a un solo efecto, el presente Acuerdo, en el
        lugar y fecha al principio indicados.
      </p>
      <Spacer size="xs" />
      <TwoSignatures
        signature1={{
          name: 'Sergio Arnaldos',
          companyName: 'Traacking World Services 22 SL',
        }}
        signature2={{
          name: user?.fullName,
          companyName: user?.company?.name,
        }}
      />
      <Spacer size="xs" />
    </LegalPage>
  )
}
